// === Provisional cover (voorlopige dekking) — single source of truth ===
// Both the end-state routing AND the "Let op" exclusion text on the accepted end page read
// from this module, so the exclusions, their data triggers, and the acceptance-answer rule
// only have to change in one place.

// Acceptance-question IDs whose "ja" (J) answer removes provisional cover and routes the
// submission to the in-behandeling end state. These are the exclusion triggers we CAN evaluate
// from the current acceptance answers:
//   schade / claims                     -> RI_EGD (any claim in the window — one is enough)
//   justice / strafrechtelijk verleden -> RI_POLJUS (verdacht van strafbaar feit) + RI_STRAFRV
//   fraude / opzettelijke misleiding    -> RI_STVOOP
//   opzegging of weigering              -> RI_OGB (opgezegd/geweigerd) + RI_GEWEIGE (ooit geweigerd)
//   aansprakelijk gesteld (AVP)         -> US_AANSPRP (nu aansprakelijk gesteld of binnenkort verwacht)
//
// TODO(client): Confirm exact question set / behaviour. Working assumption: any "ja" on these
// removes provisional cover. Flip/trim this single array to change routing in one place.
//
// NOTE: the schade question routes on ANY "ja" to RI_EGD — per the acceptance rule a single claim
// in the window is enough (it is not a "5 of meer" count). RI_SCH5JR is the personenauto mirror of
// RI_EGD, not a separate answer, so only RI_EGD is listed.
// TODO(client): the displayed "5 of meer schades" wording + the 5-vs-8-jaar window still need the
// client's confirmed numbers (the question itself currently asks about de afgelopen 5 jaar).
export const PROVISIONAL_COVER_EXCLUSION_QUESTIONS = [
  'RI_EGD',
  'RI_POLJUS',
  'RI_STRAFRV',
  'RI_STVOOP',
  'RI_OGB',
  'RI_GEWEIGE',
  'US_AANSPRP',
] as const;

// The applicant has provisional cover unless a data-backed exclusion is triggered.
export function hasProvisionalCover(answers?: Record<string, string>): boolean {
  if (!answers) return true;
  return !PROVISIONAL_COVER_EXCLUSION_QUESTIONS.some((id) => answers[id] === 'J');
}

// The rijontzegging exclusion only applies when a motorrijtuig/auto product is in the
// application. Product presence is known; the rijontzegging condition itself is not data-backed.
export function hasMotorProduct(selectedProducts?: Record<string, boolean | undefined>): boolean {
  return !!selectedProducts?.personenauto;
}

// "Let op" exclusion groups rendered on the accepted (provisional-cover) end page. The bullet
// TEXT lives in i18n (screens.endPage.provisionalCover.*) so it stays per-customer and
// translatable; this module owns the STRUCTURE + data-trigger metadata so routing and the
// displayed text share one source.
export interface ProvisionalExclusionBullet {
  i18nKey: string;
  // Only render when a motor product is in the application.
  requiresAuto?: boolean;
  // Nested sub-bullet (the betalingsachterstand 30+ dagen clarification).
  subBulletKey?: string;
  // Whether this exclusion currently drives routing (data-backed) or is informational only.
  // Kept in sync with PROVISIONAL_COVER_EXCLUSION_QUESTIONS above.
  dataBacked?: boolean;
}

export interface ProvisionalExclusionGroup {
  introKey: string;
  bullets: ProvisionalExclusionBullet[];
}

export const PROVISIONAL_COVER_EXCLUSION_GROUPS: ProvisionalExclusionGroup[] = [
  {
    introKey: 'screens.endPage.provisionalCover.group1.intro',
    bullets: [
      { i18nKey: 'screens.endPage.provisionalCover.group1.justice', dataBacked: true },
      { i18nKey: 'screens.endPage.provisionalCover.group1.fraude', dataBacked: true },
      { i18nKey: 'screens.endPage.provisionalCover.group1.opzegging', dataBacked: true },
      // TODO(data): no data for whether a rijontzegging occurred — informational only, and
      // only shown when a motor product is present.
      { i18nKey: 'screens.endPage.provisionalCover.group1.rijontzegging', requiresAuto: true },
    ],
  },
  {
    introKey: 'screens.endPage.provisionalCover.group2.intro',
    bullets: [
      // TODO(data): betalingsachterstand needs customer-record data we don't have here —
      // informational only.
      {
        i18nKey: 'screens.endPage.provisionalCover.group2.betalingsachterstand',
        subBulletKey: 'screens.endPage.provisionalCover.group2.betalingsachterstandSub',
      },
      // Routes on ANY "ja" to RI_EGD (a single claim is enough; not a "5 of meer" count).
      { i18nKey: 'screens.endPage.provisionalCover.group2.schades', dataBacked: true },
    ],
  },
];
